import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * usePagination — slice any array into pages.
 * Returns { items, page, totalPages, setPage, total }.
 * Auto-clamps to the last valid page if the input array shrinks below the current page.
 */
export function usePagination(items, pageSize = 20) {
  const [rawPage, setRawPage] = useState(1);
  const total = items?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Clamp during render — avoids set-state-in-effect.
  const page = Math.min(Math.max(1, rawPage), totalPages);

  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (items || []).slice(start, start + pageSize);
  }, [items, page, pageSize]);

  return { items: pageItems, page, totalPages, setPage: setRawPage, total, pageSize };
}

/**
 * Pagination — Prev / Next + page-of-pages indicator.
 * Renders nothing when there's only one page.
 */
export function Pagination({ page, totalPages, setPage, total, pageSize = 20, label = 'items', testIdPrefix = 'pg' }) {
  if (!totalPages || totalPages <= 1) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <div className="flex items-center justify-between gap-2 pt-3 mt-2 border-t border-border/40 text-xs">
      <span className="text-muted-foreground" data-testid={`${testIdPrefix}-range`}>
        Showing <strong>{start}–{end}</strong> of <strong>{total}</strong> {label}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page === 1}
          className="px-2 py-1 rounded border border-border/50 hover:bg-muted/40 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-0.5"
          data-testid={`${testIdPrefix}-prev`}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-3 h-3" /> Prev
        </button>
        <span className="px-2 font-medium text-foreground" data-testid={`${testIdPrefix}-page`}>
          {page} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          className="px-2 py-1 rounded border border-border/50 hover:bg-muted/40 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-0.5"
          data-testid={`${testIdPrefix}-next`}
          aria-label="Next page"
        >
          Next <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
